import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import {
  Lock, LogOut, ArrowLeft, Phone, Mail, MapPin, Ruler,
  DoorOpen, DollarSign, StickyNote, CalendarDays, Inbox as InboxIcon,
  QrCode, Plus, Download, Copy, X, Printer, MessageSquareText, ExternalLink,
  Archive, ArchiveRestore, Layers, Contact, CreditCard, Sticker,
} from "lucide-react";
import { flatPriceFor } from "./shared";
import { businessCardUrl, downloadVCard } from "./BusinessCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  new: "border-amber-500/60 bg-amber-500/10 text-amber-500",
  quoted: "border-sky-500/60 bg-sky-500/10 text-sky-400",
  accepted: "border-emerald-500/60 bg-emerald-500/10 text-emerald-400",
  done: "border-green-600/60 bg-green-500/10 text-green-400",
};
const STATUS_LABELS = { new: "New", quoted: "Quoted", accepted: "Accepted", done: "Ground" };

const CLAIM_LINE_LABELS = {
  irrigation: "irrigation/sprinklers",
  pet_fence: "pet fence",
  landscape_lighting: "landscape lighting",
  septic: "septic line",
  other: "other",
  none: "none known",
};

const IDIG_URL = "https://oups.org/idig/";

const idigBtnCls =
  "inline-flex items-center gap-2 border border-amber-500/60 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-500 transition-colors duration-150 hover:bg-amber-500/10";

function formatClaimLines(cd) {
  if (!cd || !cd.private_lines) return "";
  return cd.private_lines
    .map((k) => (k === "other" && cd.private_lines_other ? `other: "${cd.private_lines_other}"` : CLAIM_LINE_LABELS[k] || k))
    .join(", ");
}

const inputCls =
  "w-full border border-stone-700 bg-[#0c0a09] px-4 py-3 text-base sm:text-sm text-bone placeholder:text-stone-600 focus:border-amber-500 focus:outline-none transition-colors duration-150";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400";

function Row({ icon: Icon, children, testId }) {
  return (
    <div data-testid={testId} className="flex items-start gap-2.5 text-sm text-stone-300">
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
      <span className="min-w-0">{children}</span>
    </div>
  );
}

function PrintCard({ q, url }) {
  return (
    <div
      className="postcard flex w-full max-w-[5in] flex-col overflow-hidden border-2 border-amber-600 bg-white text-stone-900"
      style={{ height: "3in", printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}
    >
      <div className="h-1 shrink-0 bg-amber-600" />
      <div className="flex min-h-0 flex-1 items-center gap-3 px-4">
        <div className="min-w-0 flex-1">
          <p className="font-display text-2xl font-black uppercase leading-[0.9] tracking-tight">
            Friday
            <br />
            <span className="text-amber-600">Stumps</span>
            <span className="text-xs text-stone-400">.com</span>
          </p>
          <p className="mt-1 font-mono text-[7px] uppercase tracking-[0.22em] text-stone-500">
            Stump grinding · Fridays only · Columbus OH
          </p>
          <div className="my-2 h-px bg-amber-600/60" />
          <p className="font-mono text-[7px] font-semibold uppercase tracking-[0.18em] text-amber-600">
            Your personalized quote
          </p>
          <p className="mt-0.5 line-clamp-2 font-display text-sm font-black uppercase leading-tight text-stone-900">
            {q.address}
          </p>
          <p className="mt-1 font-display text-3xl font-black tracking-tight text-stone-900">
            ${q.price}
            <span className="ml-1.5 align-middle font-mono text-[7px] font-normal uppercase tracking-[0.15em] text-amber-600">
              flat · gone Friday
            </span>
          </p>
          <p className="mt-1 font-mono text-[7px] uppercase tracking-[0.1em] text-stone-500">
            ≈{q.stump_width_in}″ stump · no site visit · no small talk
          </p>
          <p className="mt-1.5 font-mono text-[7px] leading-relaxed text-stone-500">
            Rather text? (614) 325-1273 · eric@fridaystumps.com
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-center self-center border border-stone-200 bg-white p-2">
          <QRCodeCanvas value={url} size={130} includeMargin fgColor="#0c0a09" />
          <p className="mt-1 font-mono text-[7px] font-semibold uppercase tracking-[0.15em] text-stone-900">
            Scan me — as good as gone
          </p>
        </div>
      </div>
      <div className="h-1 shrink-0 bg-amber-600" />
    </div>
  );
}

function BlankPostcard({ testId }) {
  return (
    <div
      data-testid={testId}
      className="postcard flex flex-col overflow-hidden bg-[#0c0a09] text-bone"
      style={{ width: "6.25in", height: "4.25in", maxWidth: "100%", printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}
    >
      <div className="h-2 shrink-0 bg-amber-500" />
      <div className="flex min-h-0 flex-1 items-center gap-4 px-[0.45in]">
        <div className="min-w-0 flex-1">
          <p className="pb-0.5 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight">
            Friday
            <br />
            <span className="text-amber-500">Stumps</span>
            <span className="text-base text-stone-500">.com</span>
          </p>
          <p className="mt-2 font-mono text-[9px] uppercase leading-relaxed tracking-[0.16em] text-stone-500">
            Stump grinding · Fridays only · <span className="whitespace-nowrap">Columbus OH</span>
          </p>
          <div className="my-3 h-px bg-amber-500/50" />
          <p className="font-display text-2xl font-black uppercase leading-tight text-bone">
            Your stump. Priced.
            <br />
            Gone Friday.
          </p>
          <p className="mt-2 font-mono text-[9px] leading-relaxed tracking-[0.08em] text-stone-400">
            Scan the code to see your flat quote and lock in your Friday spot — takes 60 seconds. No site visit, no small talk.
          </p>
          <p className="mt-2.5 font-mono text-[9px] leading-relaxed text-stone-500">
            Rather text? (614) 325-1273 · eric@fridaystumps.com
          </p>
        </div>
        <div
          data-testid="sticker-zone"
          className="shrink-0 self-center"
          style={{ width: "1.75in", height: "1.75in" }}
        />
      </div>
      <div className="h-2 shrink-0 bg-amber-500" />
    </div>
  );
}

function BlankPostcardBack({ testId }) {
  return (
    <div
      data-testid={testId}
      className="postcard flex flex-col overflow-hidden bg-[#0c0a09] text-bone"
      style={{ width: "6.25in", height: "4.25in", maxWidth: "100%", printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}
    >
      <div className="h-2 shrink-0 bg-amber-500" />
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-[0.45in] text-center">
        <img
          src="/brand/fridaystumps-logo-transparent.png"
          alt="Friday Stumps logo"
          className="h-[1.5in] w-[1.5in] object-contain"
        />
        <p className="mt-3.5 font-mono text-[10px] uppercase leading-relaxed tracking-[0.16em] text-stone-500">
          Stump grinding · Forestry mulching · Fridays only · <span className="whitespace-nowrap">Columbus OH</span>
        </p>
        <p className="mt-3 font-mono text-[9px] leading-relaxed tracking-[0.1em] text-stone-400">
          (614) 325-1273 · eric@fridaystumps.com
        </p>
      </div>
      <div className="h-2 shrink-0 bg-amber-500" />
    </div>
  );
}

function StickerLabel({ q, url }) {  return (
    <div
      className="sticker-label flex flex-col items-center border border-stone-300 bg-white p-2 text-stone-900"
      style={{ width: "2in", height: "3in", printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}
    >
      <p className="font-display text-sm font-black uppercase leading-none tracking-tight">
        Friday <span className="text-amber-600">Stumps</span>
      </p>
      <div className="mt-1.5">
        <QRCodeCanvas value={url} size={150} includeMargin fgColor="#0c0a09" />
      </div>
      <p className="mt-1 font-mono text-[7px] font-semibold uppercase tracking-[0.15em] text-stone-900">
        Scan me — as good as gone
      </p>
      <p className="mt-1.5 line-clamp-2 text-center font-mono text-[7px] uppercase leading-snug tracking-[0.05em] text-stone-600">
        {q.address}
      </p>
      <p className="font-display text-xl font-black leading-tight text-stone-900">${q.price} flat</p>
    </div>
  );
}

export default function Inbox() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`)
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) {
      axios
        .get(`${API}/leads`, { params: { include_archived: true } })
        .then((r) => setLeads(r.data))
        .catch(() => setUser(false));
    }
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setUser(data);
      toast.success("Welcome back, Eric.");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed — check your credentials.");
    }
    setBusy(false);
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`).catch(() => {});
    setUser(false);
    setLeads([]);
  };

  const setStatus = async (lead, status) => {
    const id = lead._id || lead.id;
    try {
      const { data } = await axios.patch(`${API}/leads/${id}/status`, null, { params: { status } });
      setLeads((cur) => cur.map((l) => ((l._id || l.id) === id ? data : l)));
      toast.success(`Marked ${STATUS_LABELS[status].toLowerCase()}.`);
    } catch {
      toast.error("Couldn't update status.");
    }
  };

  const [priceDrafts, setPriceDrafts] = useState({});
  const [noteDrafts, setNoteDrafts] = useState({});
  const [noteOpen, setNoteOpen] = useState({});
  const [showArchivedLeads, setShowArchivedLeads] = useState(false);
  const [showArchivedQuotes, setShowArchivedQuotes] = useState(false);

  const itemId = (x) => x._id || x.id;

  const archiveItem = async (kind, item, archived) => {
    const id = itemId(item);
    try {
      const { data } = await axios.patch(`${API}/${kind}/${id}/archive`, null, { params: { archived } });
      if (kind === "leads") setLeads((cur) => cur.map((l) => (itemId(l) === id ? data : l)));
      else setQuotes((cur) => cur.map((q) => (itemId(q) === id ? data : q)));
      toast.success(archived ? "Archived — always find it below." : "Back in the active list.");
    } catch {
      toast.error("Couldn't archive that.");
    }
  };

  const saveNotes = async (kind, item) => {
    const id = itemId(item);
    try {
      const { data } = await axios.patch(`${API}/${kind}/${id}/notes`, { notes: noteDrafts[id] ?? "" });
      if (kind === "leads") setLeads((cur) => cur.map((l) => (itemId(l) === id ? data : l)));
      else setQuotes((cur) => cur.map((q) => (itemId(q) === id ? data : q)));
      setNoteOpen((cur) => ({ ...cur, [id]: false }));
      toast.success("Note saved — only you can see it.");
    } catch {
      toast.error("Couldn't save the note.");
    }
  };

  const noteBlock = (kind, item, i, prefix) => {
    const id = itemId(item);
    return (
      <div className="mt-3">
        {item.owner_notes && !noteOpen[id] && (
          <p data-testid={`${prefix}-note-${i}`} className="border-l-2 border-amber-500/60 pl-3 text-xs italic leading-relaxed text-stone-400">
            {item.owner_notes}
          </p>
        )}
        {noteOpen[id] ? (
          <div className="mt-2 space-y-2">
            <textarea
              data-testid={`${prefix}-note-input-${i}`}
              value={noteDrafts[id] ?? item.owner_notes ?? ""}
              onChange={(e) => setNoteDrafts((cur) => ({ ...cur, [id]: e.target.value }))}
              rows={2}
              placeholder="Only you see this — gate code, dog in yard, cash preferred…"
              className="w-full border border-stone-700 bg-[#0c0a09] px-3 py-2 text-base sm:text-xs text-bone placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
            />
            <div className="flex gap-2">
              <button data-testid={`${prefix}-note-save-${i}`} onClick={() => saveNotes(kind, item)} className="border border-amber-500/60 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-500 transition-colors duration-150 hover:bg-amber-500/10">
                Save note
              </button>
              <button data-testid={`${prefix}-note-cancel-${i}`} onClick={() => setNoteOpen((cur) => ({ ...cur, [id]: false }))} className="border border-stone-700 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500 transition-colors duration-150 hover:border-stone-500">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            data-testid={`${prefix}-note-toggle-${i}`}
            onClick={() => {
              setNoteDrafts((cur) => ({ ...cur, [id]: item.owner_notes ?? "" }));
              setNoteOpen((cur) => ({ ...cur, [id]: true }));
            }}
            className="mt-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500 transition-colors duration-150 hover:text-amber-500"
          >
            <StickyNote className="h-3.5 w-3.5" /> {item.owner_notes ? "Edit note" : "Add owner note"}
          </button>
        )}
      </div>
    );
  };

  const prepareAcceptLink = async (lead) => {
    const id = lead._id || lead.id;
    const draft = (priceDrafts[id] || "").trim();
    if (draft && Number(draft) !== lead.quoted_price) {
      const { data } = await axios.patch(`${API}/leads/${id}/price`, { price: Number(draft) });
      setLeads((cur) => cur.map((l) => ((l._id || l.id) === id ? data : l)));
      return { url: `${window.location.origin}/accept/${id}`, price: Number(draft) };
    }
    return { url: `${window.location.origin}/accept/${id}`, price: lead.quoted_price };
  };

  const copyAcceptLink = async (lead) => {
    try {
      const { url } = await prepareAcceptLink(lead);
      await navigator.clipboard.writeText(url);
      toast.success(`Accept link copied — text it to ${(lead.name || "").split(" ")[0] || "them"}.`);
    } catch {
      toast.error("Couldn't prepare the accept link.");
    }
  };

  const textAcceptLink = async (lead) => {
    try {
      const { url, price } = await prepareAcceptLink(lead);
      const first = (lead.name || "").split(" ")[0] || "there";
      const priceBit = price ? ` — $${price} flat` : "";
      const msg = `Hi ${first}, Eric at FridayStumps here. Your quote for the stump at ${lead.address}${priceBit}. Tap to accept and lock your Friday spot: ${url}`;
      const digits = (lead.phone || "").replace(/\D/g, "");
      toast.success("Opening your texting app…");
      window.location.href = `sms:${digits}?&body=${encodeURIComponent(msg)}`;
    } catch {
      toast.error("Couldn't prepare the accept link.");
    }
  };

  const preparePaymentLink = async (lead) => {
    const id = lead._id || lead.id;
    const draft = (priceDrafts[id] || "").trim();
    let price = lead.quoted_price;
    if (draft && Number(draft) !== lead.quoted_price) {
      const { data } = await axios.patch(`${API}/leads/${id}/price`, { price: Number(draft) });
      setLeads((cur) => cur.map((l) => ((l._id || l.id) === id ? data : l)));
      price = Number(draft);
    }
    const { data } = await axios.post(`${API}/leads/${id}/payment-link`, { origin_url: window.location.origin });
    setLeads((cur) => cur.map((l) => ((l._id || l.id) === id ? { ...l, payment_url: data.checkout_url } : l)));
    return { url: data.checkout_url, price };
  };

  const copyPaymentLink = async (lead) => {
    try {
      const { url } = await preparePaymentLink(lead);
      await navigator.clipboard.writeText(url);
      toast.success("Payment link copied — text it to the customer.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create the payment link.");
    }
  };

  const textPaymentLink = async (lead) => {
    try {
      const { url, price } = await preparePaymentLink(lead);
      const first = (lead.name || "").split(" ")[0] || "there";
      const msg = `Hi ${first}, Eric at FridayStumps — your job at ${lead.address} is done. Here's your secure card payment link for $${price}: ${url} — thanks!`;
      const digits = (lead.phone || "").replace(/\D/g, "");
      toast.success("Opening your texting app…");
      window.location.href = `sms:${digits}?&body=${encodeURIComponent(msg)}`;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create the payment link.");
    }
  };

  const [quotes, setQuotes] = useState([]);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ address: "", width: "", price: "", notes: "" });
  const [priceTouched, setPriceTouched] = useState(false);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [qrQuote, setQrQuote] = useState(null);

  const loadQuotes = () =>
    axios.get(`${API}/quotes`, { params: { include_archived: true } }).then((r) => setQuotes(r.data)).catch(() => {});

  useEffect(() => {
    if (user) loadQuotes();
  }, [user]);

  const onQuoteWidth = (e) => {
    const w = e.target.value;
    setQuoteForm((f) => ({
      ...f,
      width: w,
      price: priceTouched ? f.price : w ? String(flatPriceFor(Number(w))) : "",
    }));
  };

  const onQuotePrice = (e) => {
    setPriceTouched(true);
    setQuoteForm((f) => ({ ...f, price: e.target.value }));
  };

  const createQuote = async (e) => {
    e.preventDefault();
    setQuoteBusy(true);
    try {
      await axios.post(`${API}/quotes`, {
        address: quoteForm.address.trim(),
        stump_width_in: Number(quoteForm.width),
        price: Number(quoteForm.price),
        notes: quoteForm.notes.trim() || null,
      });
      toast.success("Quote created — QR ready to leave on the stump.");
      setQuoteForm({ address: "", width: "", price: "", notes: "" });
      setPriceTouched(false);
      setShowQuoteForm(false);
      loadQuotes();
    } catch {
      toast.error("Couldn't create quote.");
    }
    setQuoteBusy(false);
  };

  const qrValue = (q) => `${window.location.origin}/q/${q._id || q.id}`;

  const textQuoteLink = (q) => {
    const msg = `Hi — it's Eric at FridayStumps. Here's your personalized quote for the stump at ${q.address}: $${q.price}, gone Friday. Tap to view and claim it: ${qrValue(q)}`;
    window.location.href = `sms:?&body=${encodeURIComponent(msg)}`;
  };

  const downloadQr = (q) => {
    const canvas = document.getElementById("qr-canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `fridaystumps-quote-${q._id || q.id}.png`;
    a.click();
  };

  // Bare 1in x 1in QR for the quote — 300px PNG with a 300 DPI pHYs chunk
  // injected so printer apps size it to exactly 1 inch on import.
  // On phones this opens the native share sheet ("Save Image" -> Photos/camera roll);
  // on desktop it falls back to a normal file download.
  const downloadQr1in = async (q) => {
    const canvas = document.getElementById("qr-1in-canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const bin = atob(dataUrl.split(",")[1]);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const ppm = Math.round(300 / 0.0254);
    const chunk = new Uint8Array(21); // 4 length + 4 type + 9 data + 4 crc
    const dv = new DataView(chunk.buffer);
    dv.setUint32(0, 9);
    chunk.set([0x70, 0x48, 0x59, 0x73], 4); // "pHYs"
    dv.setUint32(8, ppm);
    dv.setUint32(12, ppm);
    chunk[16] = 1; // unit: meter
    let crc = ~0;
    for (let i = 4; i < 17; i++) {
      crc ^= chunk[i];
      for (let k = 0; k < 8; k++) crc = crc & 1 ? (crc >>> 1) ^ 0xedb88320 : crc >>> 1;
    }
    dv.setUint32(17, ~crc >>> 0);
    const out = new Uint8Array(bytes.length + 21);
    out.set(bytes.slice(0, 33), 0); // PNG signature + IHDR chunk
    out.set(chunk, 33);
    out.set(bytes.slice(33), 54);
    const name = `fridaystumps-quote-1in-${q._id || q.id}.png`;
    const file = new File([out], name, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      toast.info("Choose “Save Image” to add it to your Photos.");
      try {
        await navigator.share({ files: [file], title: "Quote QR (1×1)" });
      } catch {
        /* share sheet dismissed */
      }
      return;
    }
    const url = URL.createObjectURL(new Blob([out], { type: "image/png" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("QR saved — prints at exactly 1×1 inch.");
  };

  const copyQrLink = (q) => {
    navigator.clipboard?.writeText(qrValue(q));
    toast.success("Quote link copied.");
  };

  const [printQuote, setPrintQuote] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [printBatch, setPrintBatch] = useState(null);
  const [printBacks, setPrintBacks] = useState(false);
  const [shareCardOpen, setShareCardOpen] = useState(false);
  const [printBlanks, setPrintBlanks] = useState(false);
  const [printSticker, setPrintSticker] = useState(null);

  const markPrinted = async (ids) => {
    try {
      const { data } = await axios.post(`${API}/quotes/mark-printed`, { ids });
      setQuotes((cur) => cur.map((q) => data.find((d) => itemId(d) === itemId(q)) || q));
      return data.length;
    } catch {
      return 0;
    }
  };

  const toggleSelect = (id) =>
    setSelectedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const openBatchPrint = () => {
    const chosen = quotes.filter((q) => selectedIds.includes(itemId(q)));
    if (chosen.length) setPrintBatch(chosen);
  };

  const confirmBatchPrint = async () => {
    if (!printBatch) return;
    const n = await markPrinted(printBatch.map(itemId));
    if (n) toast.success(`Marked ${n} quote${n === 1 ? "" : "s"} as printed.`);
    else toast.error("Couldn't save the printed mark — printing anyway.");
    setSelectedIds([]);
    setSelectMode(false);
    window.print();
  };

  useEffect(() => {
    if (!printBatch) return;
    document.body.classList.add("batch-print-open");
    return () => document.body.classList.remove("batch-print-open");
  }, [printBatch]);

  useEffect(() => {
    if (!printBlanks) return;
    document.body.classList.add("blanks-print-open");
    const style = document.createElement("style");
    style.id = "blanks-page-style";
    style.textContent = "@page { size: 6.25in 4.25in; margin: 0; }";
    document.head.appendChild(style);
    return () => {
      document.body.classList.remove("blanks-print-open");
      style.remove();
    };
  }, [printBlanks]);

  useEffect(() => {
    if (!printSticker) return;
    document.body.classList.add("sticker-print-open");
    return () => document.body.classList.remove("sticker-print-open");
  }, [printSticker]);

  useEffect(() => {
    if (!printBacks) return;
    document.body.classList.add("backs-print-open");
    return () => document.body.classList.remove("backs-print-open");
  }, [printBacks]);

  return (
    <div data-testid="inbox-page" className="min-h-screen px-4 pb-20 pt-10 sm:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-10 flex items-center justify-between">
          <a href="/" data-testid="inbox-back-link" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500 transition-colors duration-150 hover:text-amber-500">
            <ArrowLeft className="h-3.5 w-3.5" /> fridaystumps.com
          </a>
          {user && (
            <div className="flex items-center gap-2">
              <button data-testid="share-card-btn" onClick={() => setShareCardOpen(true)} className="flex items-center gap-2 border border-amber-500/60 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-amber-500 transition-colors duration-150 hover:bg-amber-500/10">
                <Contact className="h-3.5 w-3.5" /> My card
              </button>
              <button data-testid="inbox-logout-btn" onClick={logout} className="flex items-center gap-2 border border-stone-700 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-400 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500">
                <LogOut className="h-3.5 w-3.5" /> Log out
              </button>
            </div>
          )}
        </div>

        {user === null && (
          <p className="py-24 text-center font-mono text-xs uppercase tracking-[0.25em] text-stone-500">Checking session…</p>
        )}

        {user === false && (
          <div className="mx-auto max-w-md">
            <div className="border border-stone-800 bg-charcoal p-8">
              <div className="flex items-center gap-3">
                <Lock className="h-5 w-5 text-amber-500" />
                <h1 className="font-display text-2xl font-black uppercase tracking-tight text-bone">
                  Owner Inbox
                </h1>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-stone-400">
                Every quote request lands here — photos, gate sizes, estimates.
                Owners only.
              </p>
              <form data-testid="inbox-login-form" onSubmit={login} className="mt-6 space-y-5">
                <div>
                  <label className={labelCls} htmlFor="inbox-email">Email</label>
                  <input id="inbox-email" data-testid="inbox-email-input" type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="eric@fridaystumps.com" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="inbox-password">Password</label>
                  <input id="inbox-password" data-testid="inbox-password-input" type="password" required className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                </div>
                {error && (
                  <p data-testid="inbox-login-error" className="border border-red-700/50 bg-red-500/10 px-4 py-3 font-mono text-xs text-red-400">
                    {error}
                  </p>
                )}
                <button data-testid="inbox-login-btn" type="submit" disabled={busy} className="w-full bg-amber-500 px-6 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400 active:bg-amber-600 disabled:opacity-60">
                  {busy ? "Checking…" : "Open inbox"}
                </button>
              </form>
            </div>
          </div>
        )}

        {user && (
          <>
            <div className="flex items-end justify-between border-b border-stone-800 pb-6">
              <div>
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-500">
                  Owner Dispatch
                </p>
                <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tight text-bone sm:text-4xl">
                  Quote Inbox
                </h1>
              </div>
              <span data-testid="inbox-lead-count" className="border border-stone-800 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">
                {leads.filter((l) => !l.archived).length} request{leads.filter((l) => !l.archived).length === 1 ? "" : "s"}
              </span>
            </div>

            {leads.filter((l) => !l.archived).length === 0 ? (
              <div data-testid="inbox-empty" className="flex flex-col items-center gap-4 py-24 text-center">
                <InboxIcon className="h-8 w-8 text-stone-700" />
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-stone-500">
                  No requests yet — share your link and they'll stack up here.
                </p>
              </div>
            ) : (
              <div className="mt-8 space-y-4">
                {leads.filter((l) => !l.archived).map((lead, i) => (
                  <article key={lead._id || lead.id || i} data-testid={`inbox-lead-card-${i}`} className={`border border-stone-800 bg-charcoal p-6 transition-colors duration-200 hover:border-amber-500/50 ${(lead.status || "new") === "done" ? "opacity-60" : ""}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="font-display text-xl font-bold uppercase tracking-normal text-bone sm:text-2xl">
                          {lead.name}
                        </h2>
                        {lead.job_type === "land_clearing" && (
                          <span data-testid={`lead-type-badge-${i}`} className="border border-flame/60 bg-flame/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-flame">
                            Land clearing
                          </span>
                        )}
                        <span data-testid={`lead-status-badge-${i}`} className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLES[lead.status || "new"]}`}>
                          {STATUS_LABELS[lead.status || "new"]}
                        </span>
                      </div>
                      <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {lead.created_at
                          ? new Date(lead.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                          : ""}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Row icon={Phone} testId={`lead-phone-${i}`}>
                        <a href={`tel:${(lead.phone || "").replace(/\D/g, "")}`} className="text-amber-500 underline-offset-4 hover:underline">{lead.phone}</a>
                      </Row>
                      {lead.email && (
                        <Row icon={Mail} testId={`lead-email-${i}`}>
                          <a href={`mailto:${lead.email}`} className="text-amber-500 underline-offset-4 hover:underline">{lead.email}</a>
                        </Row>
                      )}
                      <Row icon={MapPin} testId={`lead-address-${i}`}>{lead.address}</Row>
                      {lead.job_type === "land_clearing" ? (
                        <Row icon={Ruler} testId={`lead-width-${i}`}>
                          {lead.area_size || "Area TBD"}
                          {lead.terrain ? ` · ${lead.terrain} terrain` : ""}
                          {lead.clear_items?.length ? ` — ${lead.clear_items.join(", ")}` : ""}
                        </Row>
                      ) : (
                        <Row icon={Ruler} testId={`lead-width-${i}`}>
                          {[lead.stump_width_in, ...(lead.extra_stump_widths || [])].map((w) => `${w}″`).join(" + ")} — {lead.num_stumps} stump{lead.num_stumps === 1 ? "" : "s"}
                        </Row>
                      )}
                      {lead.job_type !== "land_clearing" && (
                        <Row icon={DoorOpen} testId={`lead-gate-${i}`}>
                          {lead.has_gate
                            ? `Gate — tightest opening ${lead.gate_opening_in ?? "?"}″${lead.gate_opening_in && lead.gate_opening_in < 36 ? " (tight!)" : ""}`
                            : "No gate / open access"}
                        </Row>
                      )}
                      {(lead.estimate_low || lead.estimate_high) && (
                        <Row icon={DollarSign} testId={`lead-estimate-${i}`}>
                          Site estimate ${lead.estimate_low}–${lead.estimate_high}
                        </Row>
                      )}
                      {lead.notes && (
                        <Row icon={StickyNote} testId={`lead-notes-${i}`}>{lead.notes}</Row>
                      )}
                    </div>

                    {lead.photo_paths?.length > 0 && (
                      <div data-testid={`lead-photos-${i}`} className="mt-4 flex flex-wrap gap-2">
                        {lead.photo_paths.map((p, j) => (
                          <a key={p} data-testid={`lead-photo-${i}-${j}`} href={`${BACKEND}/api/files/${p}`} target="_blank" rel="noopener noreferrer" className="group relative border border-stone-700 transition-colors duration-150 hover:border-amber-500">
                            <img src={`${BACKEND}/api/files/${p}`} alt={`Stump ${j + 1} from ${lead.name}`} className="h-20 w-20 object-cover" loading="lazy" />
                          </a>
                        ))}
                      </div>
                    )}

                    {lead.acceptance && (
                      <div data-testid={`lead-acceptance-${i}`} className="mt-4 space-y-1 border border-amber-500/30 bg-amber-500/5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.1em] text-stone-400">
                        <p>Customer accepted — private lines: <span className="text-amber-400">{formatClaimLines(lead.acceptance) || "—"}</span></p>
                        <p>Terms v{lead.acceptance.terms_version} agreed · Photos: {lead.acceptance.photo_opt_out ? "opted out" : "OK"}</p>
                      </div>
                    )}
                    {lead.acceptance && (
                      <a data-testid={`lead-idig-btn-${i}`} href={IDIG_URL} target="_blank" rel="noopener noreferrer" className={`${idigBtnCls} mt-3`}>
                        File 811 ticket (iDig) <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}

                    {noteBlock("leads", lead, i, "lead")}

                    <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-stone-800 pt-4">
                      {!["quoted", "accepted", "done"].includes(lead.status || "new") && (
                        <button data-testid={`lead-quoted-btn-${i}`} onClick={() => setStatus(lead, "quoted")} className="border border-sky-500/50 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-sky-400 transition-colors duration-150 hover:bg-sky-500/10">
                          Mark quoted
                        </button>
                      )}
                      {lead.job_type !== "land_clearing" && !lead.paid_at && (
                        <input
                          data-testid={`lead-price-input-${i}`}
                          type="number"
                          min="1"
                          max="10000"
                          placeholder="$ agreed"
                          value={priceDrafts[lead._id || lead.id] ?? (lead.quoted_price ?? "")}
                          onChange={(e) => setPriceDrafts((cur) => ({ ...cur, [lead._id || lead.id]: e.target.value }))}
                          inputMode="numeric" className="h-11 w-28 border border-stone-700 bg-[#0c0a09] px-3 py-2 font-mono text-base text-bone placeholder:text-stone-600 transition-colors duration-150 focus:border-amber-500 focus:outline-none"
                        />
                      )}
                      {lead.job_type !== "land_clearing" && !lead.acceptance && (
                        <>
                          <button data-testid={`lead-accept-link-btn-${i}`} onClick={() => copyAcceptLink(lead)} className="flex items-center gap-2 border border-amber-500/60 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-500 transition-colors duration-150 hover:bg-amber-500/10">
                            <Copy className="h-3.5 w-3.5" /> Copy accept link
                          </button>
                          <button data-testid={`lead-text-link-btn-${i}`} onClick={() => textAcceptLink(lead)} className="flex items-center gap-2 border border-amber-500/60 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-500 transition-colors duration-150 hover:bg-amber-500/10">
                            <MessageSquareText className="h-3.5 w-3.5" /> Text accept link
                          </button>
                        </>
                      )}
                      {(lead.quoted_price || (priceDrafts[lead._id || lead.id] || "").trim()) && !lead.paid_at && (
                        <>
                          <button data-testid={`lead-copy-pay-btn-${i}`} onClick={() => copyPaymentLink(lead)} className="flex items-center gap-2 border border-green-600/60 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-green-400 transition-colors duration-150 hover:bg-green-500/10">
                            <DollarSign className="h-3.5 w-3.5" /> Copy payment link
                          </button>
                          <button data-testid={`lead-text-pay-btn-${i}`} onClick={() => textPaymentLink(lead)} className="flex items-center gap-2 border border-green-600/60 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-green-400 transition-colors duration-150 hover:bg-green-500/10">
                            <MessageSquareText className="h-3.5 w-3.5" /> Text payment link
                          </button>
                        </>
                      )}
                      {lead.paid_at && (
                        <span data-testid={`lead-paid-badge-${i}`} className="flex items-center gap-2 border border-green-500/60 bg-green-500/10 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-green-400">
                          Paid{lead.quoted_price ? ` $${lead.quoted_price}` : ""}
                        </span>
                      )}
                      {(lead.status || "new") !== "done" && (
                        <button data-testid={`lead-done-btn-${i}`} onClick={() => setStatus(lead, "done")} className="border border-green-600/50 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-green-400 transition-colors duration-150 hover:bg-green-500/10">
                          Mark ground
                        </button>
                      )}
                      {(lead.status || "new") !== "new" && (
                        <button data-testid={`lead-reset-btn-${i}`} onClick={() => setStatus(lead, "new")} className="border border-stone-700 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500">
                          Back to new
                        </button>
                      )}
                      <button data-testid={`lead-archive-btn-${i}`} onClick={() => archiveItem("leads", lead, true)} className="flex items-center gap-2 border border-stone-700 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500">
                        <Archive className="h-3.5 w-3.5" /> Archive
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {leads.some((l) => l.archived) && (
              <div className="mt-8">
                <button data-testid="archived-leads-toggle" onClick={() => setShowArchivedLeads((v) => !v)} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500 transition-colors duration-150 hover:text-amber-500">
                  <Archive className="h-3.5 w-3.5" /> Archived requests ({leads.filter((l) => l.archived).length}) {showArchivedLeads ? "— hide" : "— show"}
                </button>
                {showArchivedLeads && (
                  <div className="mt-3 space-y-2">
                    {leads.filter((l) => l.archived).map((lead, i) => (
                      <div key={itemId(lead)} data-testid={`archived-lead-${i}`} className="flex flex-wrap items-center gap-x-4 gap-y-2 border border-stone-800 bg-charcoal/60 px-4 py-3 opacity-70">
                        <p className="font-display text-sm font-bold uppercase text-bone">{lead.name}</p>
                        <p className="font-mono text-[11px] text-stone-500">{lead.address}</p>
                        <span className={`border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${STATUS_STYLES[lead.status || "new"]}`}>{STATUS_LABELS[lead.status || "new"]}</span>
                        {lead.owner_notes && <p className="w-full text-xs italic text-stone-500">Note: {lead.owner_notes}</p>}
                        <button data-testid={`archived-lead-unarchive-${i}`} onClick={() => archiveItem("leads", lead, false)} className="ml-auto flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-500 hover:text-amber-400">
                          <ArchiveRestore className="h-3.5 w-3.5" /> Unarchive
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-14 border-t border-stone-800 pt-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-500">
                    Roadside Hustle
                  </p>
                  <h2 className="mt-2 font-display text-2xl font-black uppercase tracking-tight text-bone sm:text-3xl">
                    Curbside Quotes
                  </h2>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-stone-400">
                    Spot a stump while driving? Price it, show the QR, leave it
                    on the stump. They scan, they claim, you're grinding Friday.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button data-testid="batch-print-toggle-btn" onClick={() => { setSelectMode((v) => !v); setSelectedIds([]); }} className="flex items-center gap-2 border border-amber-500/60 px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-500 transition-colors duration-150 hover:bg-amber-500/10">
                    <Printer className="h-3.5 w-3.5" /> {selectMode ? "Cancel batch" : "Print batch"}
                  </button>
                  <button data-testid="print-backs-btn" onClick={() => setPrintBacks(true)} className="flex items-center gap-2 border border-stone-700 px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-300 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500">
                    <Layers className="h-3.5 w-3.5" /> Print card backs
                  </button>
                  <button data-testid="print-blanks-btn" onClick={() => setPrintBlanks(true)} className="flex items-center gap-2 border border-stone-700 px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-300 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500">
                    <CreditCard className="h-3.5 w-3.5" /> Blank card design
                  </button>
                  <button data-testid="new-quote-btn" onClick={() => setShowQuoteForm((v) => !v)} className="flex items-center gap-2 bg-amber-500 px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400 active:bg-amber-600">
                    <Plus className="h-3.5 w-3.5" /> New curbside quote
                  </button>
                </div>
              </div>

              {selectMode && (
                <div data-testid="batch-select-bar" className="mt-6 flex flex-wrap items-center gap-3 border border-amber-500/40 bg-amber-500/5 px-4 py-3">
                  <p data-testid="batch-selected-count" className="font-mono text-[11px] uppercase tracking-[0.18em] text-stone-300">
                    {selectedIds.length} selected — tick the quotes to print
                  </p>
                  <button data-testid="batch-print-btn" disabled={!selectedIds.length} onClick={openBatchPrint} className="ml-auto flex items-center gap-2 bg-amber-500 px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400 disabled:opacity-40">
                    <Printer className="h-3.5 w-3.5" /> Print {selectedIds.length || ""} card{selectedIds.length === 1 ? "" : "s"}
                  </button>
                </div>
              )}

              {showQuoteForm && (
                <form data-testid="quote-create-form" onSubmit={createQuote} className="mt-6 grid grid-cols-1 gap-4 border border-stone-800 bg-charcoal p-6 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="cq-address">Address *</label>
                    <input id="cq-address" data-testid="cq-address-input" required className={inputCls} value={quoteForm.address} onChange={(e) => setQuoteForm((f) => ({ ...f, address: e.target.value }))} placeholder="418 N High St, Worthington, OH 43085" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="cq-width">Stump width (in) *</label>
                    <input id="cq-width" data-testid="cq-width-input" required type="number" min="1" max="120" className={inputCls} value={quoteForm.width} onChange={onQuoteWidth} placeholder="26" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="cq-price">Your flat price ($) *</label>
                    <input id="cq-price" data-testid="cq-price-input" required type="number" min="1" max="10000" className={inputCls} value={quoteForm.price} onChange={onQuotePrice} placeholder="Auto from width" />
                    <p className="mt-1.5 font-mono text-[10px] tracking-[0.1em] text-stone-500">
                      Auto-calculated from width — override anytime
                    </p>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="cq-notes">Note (shows on the quote page)</label>
                    <input id="cq-notes" data-testid="cq-notes-input" className={inputCls} value={quoteForm.notes} onChange={(e) => setQuoteForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Big maple by the driveway — this Friday only at this price" />
                  </div>
                  <button data-testid="cq-submit-btn" type="submit" disabled={quoteBusy} className="bg-amber-500 px-5 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400 active:bg-amber-600 disabled:opacity-60 sm:w-auto">
                    {quoteBusy ? "Saving…" : "Create quote + QR"}
                  </button>
                </form>
              )}

              {quotes.filter((q) => !q.archived).length > 0 && (
                <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                  {quotes.filter((q) => !q.archived).map((q, i) => (
                    <article
                      key={q._id || q.id}
                      data-testid={`quote-card-${i}`}
                      onClick={selectMode ? () => toggleSelect(itemId(q)) : undefined}
                      className={`border bg-charcoal p-5 ${selectMode ? "cursor-pointer transition-colors duration-150" : ""} ${selectMode && selectedIds.includes(itemId(q)) ? "border-amber-500 ring-1 ring-amber-500/50" : "border-stone-800"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        {selectMode && (
                          <input
                            type="checkbox"
                            data-testid={`quote-select-${i}`}
                            checked={selectedIds.includes(itemId(q))}
                            onChange={() => toggleSelect(itemId(q))}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-1 h-5 w-5 shrink-0 accent-amber-500"
                            aria-label={`Select ${q.address} for printing`}
                          />
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-display text-lg font-bold uppercase text-bone">{q.address}</p>
                          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500">
                            ≈{q.stump_width_in}″ · ${q.price} flat
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {q.printed_at && (
                            <span data-testid={`quote-printed-badge-${i}`} className="border border-stone-600 bg-stone-500/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">
                              Printed
                            </span>
                          )}
                          <span data-testid={`quote-status-${i}`} className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${q.status === "claimed" ? "border-green-600/60 bg-green-500/10 text-green-400" : "border-amber-500/60 bg-amber-500/10 text-amber-500"}`}>
                            {q.status === "claimed" ? "Claimed" : "Open"}
                          </span>
                        </div>
                      </div>
                      {q.status === "claimed" && q.claimed_name && (
                        <p data-testid={`quote-claimed-by-${i}`} className="mt-3 font-mono text-[11px] tracking-[0.05em] text-green-400/90">
                          Claimed by {q.claimed_name} · <a href={`tel:${(q.claimed_phone || "").replace(/\D/g, "")}`} className="underline underline-offset-4">{q.claimed_phone}</a>
                        </p>
                      )}
                      {q.status === "claimed" && q.claim_details && (
                        <div data-testid={`quote-claim-details-${i}`} className="mt-2 space-y-1 border border-amber-500/30 bg-amber-500/5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.1em] text-stone-400">
                          <p>Private lines: <span className="text-amber-400">{formatClaimLines(q.claim_details) || "—"}</span></p>
                          <p>Terms v{q.claim_details.terms_version} agreed · Photos: {q.claim_details.photo_opt_out ? "opted out" : "OK"}</p>
                        </div>
                      )}
                      {q.status === "claimed" && (
                        <a data-testid={`quote-idig-btn-${i}`} href={IDIG_URL} target="_blank" rel="noopener noreferrer" className={`${idigBtnCls} mt-3`}>
                          File 811 ticket (iDig) <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                      {!selectMode && noteBlock("quotes", q, i, "quote")}
                      {!selectMode && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button data-testid={`quote-qr-btn-${i}`} onClick={() => setQrQuote(q)} className="flex items-center gap-2 border border-stone-700 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-300 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500">
                          <QrCode className="h-3.5 w-3.5" /> Show QR code
                        </button>
                        {q.status === "open" && (
                          <button data-testid={`quote-text-btn-${i}`} onClick={() => textQuoteLink(q)} className="flex items-center gap-2 border border-stone-700 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-300 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500">
                            <MessageSquareText className="h-3.5 w-3.5" /> Text quote link
                          </button>
                        )}
                        <button data-testid={`quote-archive-btn-${i}`} onClick={() => archiveItem("quotes", q, true)} className="flex items-center gap-2 border border-stone-700 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500">
                          <Archive className="h-3.5 w-3.5" /> Archive
                        </button>
                      </div>
                      )}
                    </article>
                  ))}
                </div>
              )}

              {quotes.some((q) => q.archived) && (
                <div className="mt-6">
                  <button data-testid="archived-quotes-toggle" onClick={() => setShowArchivedQuotes((v) => !v)} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500 transition-colors duration-150 hover:text-amber-500">
                    <Archive className="h-3.5 w-3.5" /> Archived quotes ({quotes.filter((q) => q.archived).length}) {showArchivedQuotes ? "— hide" : "— show"}
                  </button>
                  {showArchivedQuotes && (
                    <div className="mt-3 space-y-2">
                      {quotes.filter((q) => q.archived).map((q, i) => (
                        <div key={itemId(q)} data-testid={`archived-quote-${i}`} className="flex flex-wrap items-center gap-x-4 gap-y-2 border border-stone-800 bg-charcoal/60 px-4 py-3 opacity-70">
                          <p className="font-display text-sm font-bold uppercase text-bone">{q.address}</p>
                          <p className="font-mono text-[11px] text-stone-500">≈{q.stump_width_in}″ · ${q.price} · {q.status}</p>
                          {q.claimed_name && <p className="font-mono text-[11px] text-green-400/80">claimed by {q.claimed_name}</p>}
                          {q.owner_notes && <p className="w-full text-xs italic text-stone-500">Note: {q.owner_notes}</p>}
                          <button data-testid={`archived-quote-unarchive-${i}`} onClick={() => archiveItem("quotes", q, false)} className="ml-auto flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-500 hover:text-amber-400">
                            <ArchiveRestore className="h-3.5 w-3.5" /> Unarchive
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {shareCardOpen && (
          <div data-testid="card-modal" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={() => setShareCardOpen(false)}>
            <div className="w-full max-w-sm bg-white p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <p className="font-display text-lg font-black uppercase tracking-tight text-stone-900">
                  My digital card
                </p>
                <button data-testid="card-modal-close-btn" onClick={() => setShareCardOpen(false)} className="border border-stone-300 p-1.5 text-stone-500 transition-colors duration-150 hover:text-stone-900" aria-label="Close card modal">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500">
                Text it, scan it, or save it — anyone can open it
              </p>
              <div className="mt-4 flex justify-center bg-white p-2">
                <QRCodeCanvas value={businessCardUrl()} size={typeof window !== "undefined" && window.innerWidth < 400 ? 200 : 240} includeMargin fgColor="#0c0a09" />
              </div>
              <p className="mt-2 break-all text-center font-mono text-[10px] text-stone-500">{businessCardUrl()}</p>
              <div className="mt-4 flex flex-col gap-2">
                <button
                  data-testid="card-modal-text-btn"
                  onClick={() => {
                    const msg = `Eric Fisher — Friday Stumps. Stump grinding & land clearing, Columbus OH. My card: ${businessCardUrl()}`;
                    window.location.href = `sms:?&body=${encodeURIComponent(msg)}`;
                  }}
                  className="flex min-h-[48px] items-center justify-center gap-2 bg-amber-500 px-3 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400"
                >
                  <MessageSquareText className="h-3.5 w-3.5" /> Text my card
                </button>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    data-testid="card-modal-copy-btn"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(businessCardUrl());
                        toast.success("Card link copied.");
                      } catch {
                        toast.error("Couldn't copy the link.");
                      }
                    }}
                    className="flex min-h-[48px] flex-1 items-center justify-center gap-2 border border-stone-300 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-700 transition-colors duration-150 hover:border-stone-500"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy link
                  </button>
                  <button
                    data-testid="card-modal-vcard-btn"
                    onClick={downloadVCard}
                    className="flex min-h-[48px] flex-1 items-center justify-center gap-2 border border-stone-300 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-700 transition-colors duration-150 hover:border-stone-500"
                  >
                    <Download className="h-3.5 w-3.5" /> Contact file
                  </button>
                </div>
                <a
                  href="/card"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="card-modal-open-btn"
                  className="flex min-h-[44px] items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500 transition-colors duration-150 hover:text-stone-900"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Open card page
                </a>
              </div>
            </div>
          </div>
        )}

        {qrQuote && (
          <div data-testid="qr-modal" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={() => setQrQuote(null)}>            <div className="w-full max-w-sm bg-white p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <p className="font-display text-lg font-black uppercase tracking-tight text-stone-900">
                  FridayStumps.com
                </p>
                <button data-testid="qr-close-btn" onClick={() => setQrQuote(null)} className="border border-stone-300 p-1.5 text-stone-500 transition-colors duration-150 hover:text-stone-900" aria-label="Close QR">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-4 flex justify-center bg-white p-2">
                <QRCodeCanvas id="qr-canvas" value={qrValue(qrQuote)} size={typeof window !== "undefined" && window.innerWidth < 400 ? 220 : 280} includeMargin fgColor="#0c0a09" />
              </div>
              <p className="mt-3 text-center font-display text-xl font-black uppercase text-stone-900">
                ${qrQuote.price} — this stump, gone Friday
              </p>
              <p className="mt-1 break-all text-center font-mono text-[10px] text-stone-500">{qrValue(qrQuote)}</p>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <button data-testid="qr-download-btn" onClick={() => downloadQr(qrQuote)} className="flex min-h-[48px] flex-1 items-center justify-center gap-2 bg-amber-500 px-3 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400">
                  <Download className="h-3.5 w-3.5" /> Save PNG
                </button>
                <button data-testid="quote-save-qr-btn" onClick={() => downloadQr1in(qrQuote)} className="flex min-h-[48px] flex-1 items-center justify-center gap-2 border border-stone-300 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-700 transition-colors duration-150 hover:border-stone-500">
                  <Download className="h-3.5 w-3.5" /> Save QR (1×1)
                </button>
                <button data-testid="qr-copy-btn" onClick={() => copyQrLink(qrQuote)} className="flex min-h-[48px] flex-1 items-center justify-center gap-2 border border-stone-300 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-700 transition-colors duration-150 hover:border-stone-500">
                  <Copy className="h-3.5 w-3.5" /> Copy link
                </button>
                <button data-testid="qr-print-btn" onClick={() => { setPrintQuote(qrQuote); setQrQuote(null); }} className="flex min-h-[48px] flex-1 items-center justify-center gap-2 border border-stone-300 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-700 transition-colors duration-150 hover:border-stone-500">
                  <Printer className="h-3.5 w-3.5" /> Print card
                </button>
                <button data-testid="quote-sticker-btn" onClick={() => setPrintSticker(qrQuote)} className="flex min-h-[48px] flex-1 items-center justify-center gap-2 border border-stone-300 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-700 transition-colors duration-150 hover:border-stone-500">
                  <Sticker className="h-3.5 w-3.5" /> Print sticker
                </button>
              </div>
              <div className="hidden">
                <QRCodeCanvas id="qr-1in-canvas" value={qrValue(qrQuote)} size={300} includeMargin fgColor="#000000" bgColor="#ffffff" />
              </div>
            </div>
          </div>
        )}

        {printSticker &&
          createPortal(
            <div id="qr-print-sticker" data-testid="sticker-print-overlay" className="fixed inset-0 z-[120] overflow-y-auto bg-white p-6">
              <div className="no-print flex justify-end gap-2 pb-4">
                <button data-testid="sticker-print-confirm-btn" onClick={() => window.print()} className="bg-amber-500 px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400">
                  Print / Save PDF
                </button>
                <button data-testid="sticker-print-close-btn" onClick={() => setPrintSticker(null)} className="border border-stone-300 bg-white px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-600 transition-colors duration-150 hover:border-stone-500">
                  Close
                </button>
              </div>
              <div className="flex justify-center pt-6">
                <StickerLabel q={printSticker} url={qrValue(printSticker)} />
              </div>
            </div>,
            document.body
          )}

        {printQuote && (
          <div data-testid="qr-print-card" id="qr-print-card" className="fixed inset-0 z-[110] flex items-center justify-center bg-white p-6">
            <div className="no-print absolute right-4 top-4 flex gap-2">
              <button data-testid="print-card-btn" onClick={async () => { await markPrinted([itemId(printQuote)]); window.print(); }} className="bg-amber-500 px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400">
                Print / Save PDF
              </button>
              <button data-testid="print-close-btn" onClick={() => setPrintQuote(null)} className="border border-stone-300 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-600 transition-colors duration-150 hover:border-stone-500">
                Close
              </button>
            </div>
            <div className="w-full max-w-[5in]">
              <PrintCard q={printQuote} url={qrValue(printQuote)} />
            </div>
          </div>
        )}

        {printBatch &&
          createPortal(
            <div id="qr-print-batch" data-testid="qr-print-batch" className="fixed inset-0 z-[110] overflow-y-auto bg-white p-6">
              <div className="no-print sticky top-0 z-10 flex justify-end gap-2 pb-4">
                <button data-testid="batch-print-confirm-btn" onClick={confirmBatchPrint} className="bg-amber-500 px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400">
                  Print / Save PDF — {printBatch.length} card{printBatch.length === 1 ? "" : "s"}
                </button>
                <button data-testid="batch-print-close-btn" onClick={() => setPrintBatch(null)} className="border border-stone-300 bg-white px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-600 transition-colors duration-150 hover:border-stone-500">
                  Close
                </button>
              </div>
              {Array.from({ length: Math.ceil(printBatch.length / 3) }, (_, gi) => (
                <div key={gi} data-testid={`print-sheet-${gi}`} className="print-sheet mx-auto mb-6 flex w-full max-w-[7.8in] flex-col items-center gap-[0.3in] sm:h-[10.3in] sm:justify-center">
                  {printBatch.slice(gi * 3, gi * 3 + 3).map((q) => (
                    <PrintCard key={itemId(q)} q={q} url={qrValue(q)} />
                  ))}
                </div>
              ))}
            </div>,
            document.body
          )}

        {printBlanks &&
          createPortal(
            <div id="qr-print-blanks" data-testid="blank-print-overlay" className="fixed inset-0 z-[110] overflow-y-auto bg-stone-100 p-6">
              <div className="no-print sticky top-0 z-10 flex flex-wrap items-center justify-end gap-2 pb-4">
                <p className="mr-auto font-mono text-[11px] uppercase tracking-[0.15em] text-stone-600">
                  Saves as a 2-page PDF (front + back), 4.25in × 6.25in with bleed — upload both pages to the print company (trims to 4×6)
                </p>
                <button data-testid="blanks-print-confirm-btn" onClick={() => window.print()} className="bg-amber-500 px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400">
                  Save PDF
                </button>
                <button data-testid="blanks-print-close-btn" onClick={() => setPrintBlanks(false)} className="border border-stone-300 bg-white px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-600 transition-colors duration-150 hover:border-stone-500">
                  Close
                </button>
              </div>
              <div className="blank-sheet flex flex-col items-center pt-8">
                <p className="no-print mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Front</p>
                <BlankPostcard testId="blank-card-0" />
              </div>
              <div className="blank-sheet mt-8 flex flex-col items-center">
                <p className="no-print mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Back</p>
                <BlankPostcardBack testId="blank-card-back" />
              </div>
            </div>,
            document.body
          )}

        {printBacks &&
          createPortal(
            <div id="qr-print-backs" data-testid="qr-print-backs" className="fixed inset-0 z-[110] overflow-y-auto bg-white p-6">
              <div className="no-print sticky top-0 z-10 flex flex-wrap items-center justify-end gap-2 pb-4">
                <p className="mr-auto font-mono text-[11px] uppercase tracking-[0.15em] text-stone-500">
                  Print this on the back of your QR quote sheets — flip on the long edge
                </p>
                <button data-testid="backs-print-confirm-btn" onClick={() => window.print()} className="bg-amber-500 px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400">
                  Print / Save PDF
                </button>
                <button data-testid="backs-print-close-btn" onClick={() => setPrintBacks(false)} className="border border-stone-300 bg-white px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-600 transition-colors duration-150 hover:border-stone-500">
                  Close
                </button>
              </div>
              <div data-testid="backs-sheet" className="backs-sheet mx-auto flex w-full max-w-[7.8in] flex-wrap content-start overflow-hidden" style={{ height: "10.3in" }}>
                {Array.from({ length: 40 }, (_, i) => (
                  <div key={i} data-testid={`backs-tile-${i}`} className="flex items-center justify-center" style={{ width: "25%", height: "1.03in" }}>
                    <p className="select-none font-display text-xl font-black uppercase leading-none tracking-tight" style={{ transform: "rotate(-8deg)" }}>
                      <span className="text-stone-900">Friday</span>
                      <span className="text-amber-600">Stumps</span>
                      <span className="text-[10px] text-stone-400">.com</span>
                    </p>
                  </div>
                ))}
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
}
